import React from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  type MRT_ColumnFiltersState,
} from "material-react-table";
import { CircularProgress, Typography } from "@mui/material";

interface ReusableTableProps<T extends Record<string, any>> {
  columns: MRT_ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  error?: string | null;
  rowCount?: number;
  pagination?: MRT_PaginationState;
  onPaginationChange?: (
    updater:
      | MRT_PaginationState
      | ((prev: MRT_PaginationState) => MRT_PaginationState)
  ) => void;
  enablePagination?: boolean;
  enableSorting?: boolean;
  enableColumnFilters?: boolean;
  enableGlobalFilter?: boolean;
  enableFullScreenToggle?: boolean;
  enableDensityToggle?: boolean;
  enableHiding?: boolean;
  darkBorder?: boolean;
  renderTopToolbarCustomActions?: (props: { table: any }) => React.ReactNode;
  renderEmptyRowsFallback?: () => React.ReactNode;
}

const ReusableTable = <T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  error = null,
  rowCount,
  pagination,
  onPaginationChange,
  enablePagination = true,
  enableSorting = true,
  enableColumnFilters = true,
  enableGlobalFilter = false,
  enableFullScreenToggle = false,
  enableDensityToggle = false,
  enableHiding = false,
  darkBorder = false,
  renderTopToolbarCustomActions,
  renderEmptyRowsFallback,
}: ReusableTableProps<T>) => {
  const [columnFilters, setColumnFilters] =
    React.useState<MRT_ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] =
    React.useState<Record<string, boolean>>({});

  const allColumnsHidden = React.useMemo(() => {
    const visibleColumns = Object.values(columnVisibility).filter(
      (v) => v !== false
    );
    return visibleColumns.length === 0 && Object.keys(columnVisibility).length > 0;
  }, [columnVisibility]);

  const enableTopToolbar =
    enableGlobalFilter ||
    enableFullScreenToggle ||
    enableDensityToggle ||
    enableHiding;

  const table = useMaterialReactTable({
    columns,
    data,
    enablePagination,
    enableSorting,
    enableColumnFilters,
    enableGlobalFilter,
    enableFullScreenToggle,
    enableDensityToggle,
    enableHiding,
    enableColumnActions: (enableColumnFilters || enableSorting),
    enableStickyHeader: false,

    enableTopToolbar: false,

    state: {
      columnFilters,
      columnVisibility,
      ...(enablePagination && {
        pagination: pagination ?? { pageIndex: 0, pageSize: 10 },
      }),
      // density: "compact",
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        enableHiding: true,
        grow: true,
        muiTableBodyCellProps: {
          sx: {
            fontFamily: "Inter,sans-serif",
            maxWidth: "60px"
          }
        },
        muiTableHeadCellProps: {
          sx: {
            fontFamily: "Inter,sans-serif",
            maxWidth: "60px"
          }
        }
      },
    },

    columnFilterDisplayMode: "subheader",
    enableColumnFilterModes: false,
    initialState: {
      showColumnFilters: false,
      columnFilters: [],
    },


    muiFilterTextFieldProps: {
      size: "small",
      variant: "standard",
    },

    manualPagination: enablePagination && rowCount !== undefined,
    rowCount: enablePagination ? rowCount : undefined,
    onPaginationChange: enablePagination ? onPaginationChange : undefined,
    enableBottomToolbar: enablePagination && !allColumnsHidden,
    enableTableFooter: false,

    layoutMode: 'grid',
    defaultColumn: {
      maxSize: 9001,
      size: 260,
      muiTableBodyCellProps: {
        sx: {
          fontFamily: "Inter,sans-serif"
        }
      },
    },

    muiTableContainerProps: {
      sx: {
        overflowY: "visible",
        boxShadow: "none",
        borderBottom: "none", // ⬅️ ensure no bottom divider
      },
    },

    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        ...(darkBorder && {
          border: "1px solid rgb(172,172,172)",
          marginBottom: "15px",
        }),
      },
    },
    muiTableHeadCellProps: {
      sx: {
        minHeight: "56px",
        height: "auto",
        paddingTop: "8px",
        paddingBottom: "8px",
        alignItems: "flex-start",
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: "#FFFFFF",
        border: "none",
        "& .MuiBox-root": {
          border: "none",
        },
      },
    },
    positionToolbarAlertBanner: 'none',
    enableFilterMatchHighlighting: true,

    renderEmptyRowsFallback: renderEmptyRowsFallback
      ? renderEmptyRowsFallback
      : () =>
        allColumnsHidden ? (
          <div className="flex justify-center items-center p-8">
            <Typography variant="body1" color="textSecondary">
              All columns are hidden. Use the column visibility option to show
              columns.
            </Typography>
          </div>
        ) : null,
  });

  return (
    <div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <CircularProgress sx={{ color: "#757575" }}/>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <MaterialReactTable table={table} />
          {allColumnsHidden && (
            <div className="flex justify-center items-center p-4 bg-gray-50 border-t">
              <Typography variant="body2" color="textSecondary">
                💡 Tip: All columns are currently hidden. Use the columns option
                to show them.
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReusableTable;
